import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { weakAreasAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
    AlertTriangle,
    Target,
    Brain,
    Lightbulb,
    BarChart3,
    BookOpen,
    TrendingDown,
    Flame,
    AlertCircle,
    CheckCircle
} from 'lucide-react';
import Layout from '../components/Layout';

export default function WeakAreas() {
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchReport();
    }, []);

    const fetchReport = async () => {
        try {
            const response = await weakAreasAPI.getReport();
            setReport(response.data);
        } catch (err) {
            console.error('Failed to fetch weak areas report:', err);
            toast.error('Failed to load analysis');
        } finally {
            setLoading(false);
        }
    };

    const getAccuracyColor = (accuracy) => {
        if (accuracy >= 80) return 'text-emerald-600';
        if (accuracy >= 60) return 'text-amber-600';
        return 'text-red-600';
    };

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'high':
                return <Flame className="h-4 w-4 text-red-500" />;
            case 'medium':
                return <AlertCircle className="h-4 w-4 text-amber-500" />;
            default:
                return <AlertTriangle className="h-4 w-4 text-slate-400" />;
        }
    };

    const getPriorityBadge = (priority) => {
        switch (priority) {
            case 'high':
                return <Badge className="bg-red-100 text-red-700 text-xs">High Priority</Badge>;
            case 'medium':
                return <Badge className="bg-amber-100 text-amber-700 text-xs">Medium</Badge>;
            default:
                return <Badge className="bg-slate-100 text-slate-600 text-xs">Low</Badge>;
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-64" />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Skeleton className="h-32" />
                        <Skeleton className="h-32" />
                        <Skeleton className="h-32" />
                    </div>
                    <Skeleton className="h-64" />
                </div>
            </Layout>
        );
    }

    if (!report?.has_data) {
        return (
            <Layout>
                <div className="max-w-2xl mx-auto text-center py-12" data-testid="weak-areas-empty">
                    <Brain className="h-16 w-16 mx-auto mb-6 text-slate-300" />
                    <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                        Not Enough Data Yet
                    </h1>
                    <p className="text-slate-500 mb-6">
                        Answer more questions to generate your weak areas analysis.
                        We need at least a few practice sessions to identify patterns.
                    </p>
                    <Button onClick={() => navigate('/practice')}>
                        <BookOpen className="mr-2 h-4 w-4" />
                        Start Practicing
                    </Button>
                </div>
            </Layout>
        );
    }

    const overallStats = report.overall_stats || {};
    const weakConcepts = report.weak_concepts || [];
    const subjectOverview = report.subject_overview || {};

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="weak-areas-report">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                            Concepts Needing Attention
                        </h1>
                        <p className="text-slate-500">Specific concepts you're consistently getting wrong</p>
                    </div>
                    <Button variant="outline" onClick={() => navigate('/analytics')}>
                        <BarChart3 className="mr-2 h-4 w-4" />
                        Full Analytics
                    </Button>
                </div>

                {/* Overall Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-slate-900">{overallStats.total_attempts || 0}</p>
                            <p className="text-sm text-slate-500">Questions Attempted</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-emerald-600">{overallStats.total_correct || 0}</p>
                            <p className="text-sm text-slate-500">Correct</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-red-500">
                                {(overallStats.total_attempts || 0) - (overallStats.total_correct || 0)}
                            </p>
                            <p className="text-sm text-slate-500">Incorrect</p>
                        </CardContent>
                    </Card>
                    <Card className={cn(
                        "border-2",
                        (overallStats.overall_accuracy || 0) >= 70 ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"
                    )}>
                        <CardContent className="p-4 text-center">
                            <p className={cn("text-3xl font-bold", getAccuracyColor(overallStats.overall_accuracy || 0))}>
                                {overallStats.overall_accuracy || 0}%
                            </p>
                            <p className="text-sm text-slate-500">Overall Accuracy</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Recommendations */}
                {report.recommendations?.length > 0 && (
                    <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope' }}>
                                <Lightbulb className="h-5 w-5 text-amber-500" />
                                Study Recommendations
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ul className="space-y-2">
                                {report.recommendations.map((rec, i) => (
                                    <li key={i} className="flex items-start gap-2 text-slate-700">
                                        <Target className="h-4 w-4 mt-1 text-amber-500 flex-shrink-0" />
                                        {rec}
                                    </li>
                                ))}
                            </ul>
                        </CardContent>
                    </Card>
                )}

                {/* Weak Concepts - Main Focus */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                            <TrendingDown className="h-5 w-5 text-red-500" />
                            Concepts You're Struggling With
                        </CardTitle>
                        <CardDescription>
                            These are specific topics where you've missed questions multiple times
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {weakConcepts.length > 0 ? (
                            <div className="space-y-4">
                                {weakConcepts.map((item, i) => (
                                    <div 
                                        key={i} 
                                        className={cn(
                                            "p-4 rounded-lg border",
                                            item.priority === 'high' ? 'border-red-200 bg-red-50/50' :
                                            item.priority === 'medium' ? 'border-amber-200 bg-amber-50/50' :
                                            'border-slate-200 bg-slate-50/50'
                                        )}
                                    >
                                        <div className="flex items-start justify-between gap-4 mb-2">
                                            <div className="flex items-center gap-2">
                                                {getPriorityIcon(item.priority)}
                                                <span className="font-medium text-slate-900">{item.concept}</span>
                                            </div>
                                            {getPriorityBadge(item.priority)}
                                        </div>
                                        
                                        <div className="mt-3">
                                            <div className="flex items-center justify-between text-sm mb-1">
                                                <span className="text-slate-500">Accuracy</span>
                                                <span className={cn("font-semibold", getAccuracyColor(item.accuracy))}>
                                                    {item.accuracy}%
                                                </span>
                                            </div>
                                            <Progress 
                                                value={item.accuracy} 
                                                className="h-2"
                                            />
                                        </div>
                                        
                                        <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
                                            <span>
                                                <strong>{item.total_attempts}</strong> attempts
                                            </span>
                                            <span>
                                                <strong className="text-red-600">{item.times_missed}</strong> times missed
                                            </span>
                                            {item.consecutive_misses > 0 && (
                                                <span className="text-red-600 font-medium">
                                                    🔥 {item.consecutive_misses} in a row
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-slate-500">
                                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-400" />
                                <p className="font-medium text-slate-700">Great job!</p>
                                <p className="text-sm">No weak concepts detected. Keep up the good work!</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Subject Overview */}
                {Object.keys(subjectOverview).length > 0 && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope' }}>
                                <BarChart3 className="h-5 w-5 text-indigo-500" />
                                Subject Overview
                            </CardTitle>
                            <CardDescription>Your performance breakdown by subject</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {Object.entries(subjectOverview).map(([subject, stats]) => (
                                    <div 
                                        key={subject}
                                        className="p-3 rounded-lg border border-slate-200 bg-slate-50/50"
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-medium text-slate-800 text-sm">{subject}</span>
                                            <span className={cn("font-bold", getAccuracyColor(stats.accuracy))}>
                                                {stats.accuracy}%
                                            </span>
                                        </div>
                                        <Progress value={stats.accuracy} className="h-1.5" />
                                        <p className="text-xs text-slate-400 mt-1">
                                            {stats.correct}/{stats.total} correct
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4 justify-center pt-4">
                    <Button onClick={() => navigate('/practice')}>
                        <BookOpen className="mr-2 h-4 w-4" />
                        Practice Questions
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/study-planner')}>
                        <Target className="mr-2 h-4 w-4" />
                        Study Planner
                    </Button>
                </div>
            </div>
        </Layout>
    );
}
